import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useSearchParams } from "react-router-dom";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

// Shared TextField sx — dark bg, green focus border, cream text
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgDark,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.borderSubtle },
    "&:hover fieldset": { borderColor: DL.borderGreen },
    "&.Mui-focused fieldset": { borderColor: DL.green, borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": {
    color: DL.textPrimary,
    fontSize: "0.9rem",
    backgroundColor: `${DL.bgDark} !important`,
  },
  "& .MuiInputLabel-root": { color: DL.textSecondary, fontSize: "0.9rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
  "& .MuiFormHelperText-root": { color: DL.red, fontSize: "0.75rem" },
  "& input:-webkit-autofill": {
    WebkitBoxShadow: `0 0 0 1000px ${DL.bgDark} inset !important`,
    WebkitTextFillColor: `${DL.textPrimary} !important`,
  },
};

const ConditionList = ({ conditions }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1, mb: 0.5 }}>
    {conditions.map((c, i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {c.isValid
          ? <CheckIcon sx={{ fontSize: 13, color: DL.green }} />
          : <CloseIcon sx={{ fontSize: 13, color: DL.red }} />}
        <Typography sx={{ fontSize: "0.75rem", color: c.isValid ? DL.green : DL.textMuted }}>
          {c.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const passwordConditions = [
    { label: "At least 8 characters", isValid: password.length >= 8 },
    { label: "Contains a number", isValid: /\d/.test(password) },
    { label: "Contains an uppercase letter", isValid: /[A-Z]/.test(password) },
    { label: "Contains a lowercase letter", isValid: /[a-z]/.test(password) },
    { label: "Contains a special character", isValid: /[!@#$%^&*]/.test(password) },
  ];

  const allConditionsMet = passwordConditions.every((c) => c.isValid);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const PasswordToggle = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPassword((p) => !p)}
        edge="end"
        sx={{ color: DL.textMuted }}
      >
        {showPassword
          ? <Visibility sx={{ fontSize: 18 }} />
          : <VisibilityOff sx={{ fontSize: 18 }} />}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!allConditionsMet) {
      setErrorMessage("Please ensure your password meets all requirements.");
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, newPassword: password }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message);
      } else {
        setErrorMessage(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Invalid token state ────────────────────────────────────────────────────
  if (!token) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
        }}
      >
        {/* Back link */}
        <Box
          onClick={() => navigate("/signin")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 2,
            cursor: "pointer",
            color: DL.textMuted,
            "&:hover": { color: DL.green },
            transition: "color 0.2s ease",
            alignSelf: "flex-start",
            width: "100%",
            maxWidth: 480,
          }}
        >
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            ← Back to sign in
          </Typography>
        </Box>

        {/* Card */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            backgroundColor: DL.bgMedium,
            border: `1px solid ${DL.border}`,
            borderRadius: "12px",
            boxShadow: "0 4px 40px rgba(0,0,0,0.4), 0 0 80px rgba(98,244,129,0.04)",
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{ fontWeight: 800, fontSize: "1.5rem", color: DL.textPrimary, mb: 2 }}
          >
            Invalid Reset Link
          </Typography>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: "8px",
              backgroundColor: "rgba(255,100,103,0.1)",
              color: DL.red,
              border: "1px solid rgba(255,100,103,0.25)",
              "& .MuiAlert-icon": { color: DL.red },
            }}
          >
            This password reset link is invalid or has expired.
          </Alert>
          <Button
            fullWidth
            onClick={() => navigate("/forgot-password")}
            sx={{
              background: "linear-gradient(to right, #10b981, #059669)",
              color: "#fff",
              borderRadius: "8px",
              py: 1.4,
              fontWeight: 700,
              fontSize: "0.95rem",
              textTransform: "none",
              boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
              "&:hover": { filter: "brightness(110%)" },
              transition: "all 0.2s ease",
            }}
          >
            Request New Reset Link
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Main reset form ────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 5,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
      }}
    >
      {/* Back link */}
      <Box
        onClick={() => navigate("/signin")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 2,
          cursor: "pointer",
          color: DL.textMuted,
          "&:hover": { color: DL.green },
          transition: "color 0.2s ease",
          alignSelf: "flex-start",
          width: "100%",
          maxWidth: 480,
        }}
      >
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
          ← Back to sign in
        </Typography>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.4), 0 0 80px rgba(98,244,129,0.04)",
          p: 4,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1.5rem",
            color: DL.textPrimary,
            textAlign: "center",
            mb: 0.5,
          }}
        >
          Reset Password
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: DL.textSecondary,
            textAlign: "center",
            mb: 3,
          }}
        >
          Enter your new password below.
        </Typography>

        {/* Success state */}
        {successMessage && (
          <Box sx={{ textAlign: "center" }}>
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: "8px",
                backgroundColor: "rgba(98,244,129,0.1)",
                color: DL.green,
                "& .MuiAlert-icon": { color: DL.green },
              }}
            >
              {successMessage}
            </Alert>
            <Button
              fullWidth
              onClick={() => navigate("/signin")}
              sx={{
                background: "linear-gradient(to right, #10b981, #059669)",
                color: "#fff",
                borderRadius: "8px",
                py: 1.4,
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "none",
                boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
                "&:hover": { filter: "brightness(110%)" },
                transition: "all 0.2s ease",
              }}
            >
              Go to Sign In
            </Button>
          </Box>
        )}

        {/* Form */}
        {!successMessage && (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {errorMessage && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,100,103,0.1)",
                  color: DL.red,
                  border: "1px solid rgba(255,100,103,0.25)",
                  "& .MuiAlert-icon": { color: DL.red },
                }}
              >
                {errorMessage}
              </Alert>
            )}

            <Box>
              <TextField
                required
                fullWidth
                name="password"
                label="New Password"
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                InputProps={{ endAdornment: PasswordToggle }}
                sx={inputSx}
              />
              {password.length > 0 && <ConditionList conditions={passwordConditions} />}
            </Box>

            <TextField
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type={showPassword ? "text" : "password"}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              error={confirmPassword.length > 0 && !passwordsMatch}
              helperText={
                confirmPassword.length > 0 && !passwordsMatch
                  ? "Passwords do not match"
                  : ""
              }
              InputProps={{ endAdornment: PasswordToggle }}
              sx={inputSx}
            />

            <Button
              type="submit"
              fullWidth
              disabled={loading || !allConditionsMet || !passwordsMatch}
              sx={{
                background: "linear-gradient(to right, #10b981, #059669)",
                color: "#fff",
                borderRadius: "8px",
                py: 1.4,
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "none",
                boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
                "&:hover": { filter: "brightness(110%)" },
                "&.Mui-disabled": {
                  background: DL.bgLight,
                  color: DL.textMuted,
                  boxShadow: "none",
                },
                transition: "all 0.2s ease",
                mt: 0.5,
              }}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Typography sx={{ mt: 3, fontSize: "0.75rem", color: DL.textMuted }}>
        © {new Date().getFullYear()} StonksCentral. All rights reserved.
      </Typography>
    </Box>
  );
}

export default ResetPasswordPage;
